<template>
  <div :style="cssVar" :key="componentKey">
    <template v-if="dataAvailable">
      <div class="total-data-chart">
        <e-chart :option="totalOption" autoresize />
      </div>
      <div class="single-data-chart">
        <e-chart :option="singleOption" autoresize />
      </div>
    </template>
    <div v-else class="alert alert-info">{{ $t('reporting.noDataForSelectedRange') }}</div>
  </div>
</template>

<script>
import stringUtils from '@/components/mixins/stringUtils'

export default {
  name: 'ActionsDataBarChart',
  props: {
    entries: {
      type: Array,
      required: true,
    },
    totalCount: {
      type: Number,
      required: true,
    },
    title: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      componentKey: 0,
    }
  },
  watch: {
    entries() {
      // Force re-render of the chart so that its height is recalculated.
      this.componentKey += 1
    },
  },
  mixins: [stringUtils],
  computed: {
    cssVar() {
      return {
        // 26px bar height + 12px space = 38px in total
        '--chart-height': `${this.entries.length * 38}px`,
      }
    },
    dataAvailable() {
      return this.totalCount > 0
    },
    totalOption() {
      const series = [{ value: this.totalCount }]
      let yAxis = []

      if (this.title) {
        yAxis = [this.title]
      } else {
        yAxis = [this.$t('meta.total')]
      }

      return this.barGraphOptions(
        yAxis,
        series,
        {
          lineHeight: 16,
        },
        Math.max(series.map(entry => entry.value)),
      )
    },
    singleOption() {
      let series = []

      series = Object.values(this.entries).map(item => {
        return { name: item.name, value: item.count }
      })

      // Greater count/value numbers shall appear first in the list.
      // Needs to also be addressed in the bar chart option 'yAxis.inverse'.
      series.sort((a, b) => b.value - a.value)

      return this.barGraphOptions(
        series.map(entry => entry.name),
        series,
        {
          overflow: 'truncate',
          ellipsis: '...',
        },
      )
    },
  },
  methods: {
    barGraphOptions(yAxisData, seriesData, axisLabel, maxValue) {
      return {
        // animationDuration: 0 is a hack. If we rerender the component
        // with the same data, the users sees a bar filling animation
        // which we don't want. Disabling animations quickfixes this.
        animationDuration: 0,
        xAxis: {
          type: 'value',
          show: false,
          max: maxValue,
        },
        yAxis: {
          inverse: true, // Display longer bars at top
          type: 'category',
          data: yAxisData,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            margin: 24,
            fontWeight: '700',
            fontSize: 16,
            fontFamily: 'Source Sans Pro',
            color: '#314053',
            width: 120,
            ...axisLabel,
          },
        },
        grid: {
          top: 0,
          bottom: 0,
          left: '150px',
          right: 0,
        },
        series: [
          {
            data: seriesData,
            type: 'bar',
            barWidth: 26,
            backgroundStyle: {
              borderRadius: 4,
              color: '#a1a1a1',
            },
            barCategoryGap: 12,
            itemStyle: {
              borderRadius: 4,
              color: '#314053',
            },
            label: {
              show: true,
              position: ['60px', '6px'],
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
              fontFamily: 'Source Sans Pro',
              align: 'right',
            },
            showBackground: true,
          },
        ],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.single-data-chart {
  height: var(--chart-height);
}

.total-data-chart {
  height: 2rem;
  margin-bottom: 1.5rem;
}
</style>
